import type { ApiTrackingLabels, KnownTrackingLabels, TrackingLabels } from '@shared/tracking/TrackingLabels';
import type { IntentResult, SearchContext } from '@shared/types/types';
import type { GalleryLayout } from '@tilelist/domain/composition/GalleryLayout.ts';
import type { OnEx } from '@tilelist/domain/experiments/OnEx';
import { extractComparisonId } from '@tilelist/domain/feature-visibility/ComparisonService';
import { shouldShowTopInfos } from '@tilelist/domain/feature-visibility/TopInfoVisibility';
import { experimentSegmentTracking } from './ExperimentTracking.ts';

type ComparisonBaseTrackingLabels = Pick<KnownTrackingLabels, 'san_Comparison'>;

type HeaderTypeBaseTrackingLabels = Pick<KnownTrackingLabels, 'san_HeaderType'>;

type ListFeedbackToolBaseTrackingLabels = Pick<KnownTrackingLabels, 'wk.san_ListFeedbackTool'>;

type TopInfoBaseTrackingLabels = Pick<KnownTrackingLabels, 'wk.san_TopInfoList'>;

type PaginationBaseTrackingLabels = Pick<
    KnownTrackingLabels,
    'wk.san_ListOrder' | 'wk.san_PageNumber' | 'wk.san_CountPages'
>;

type AdditionalBaseTrackingLabels = Pick<KnownTrackingLabels, 'san_HeaderType' | 'wk.san_ListFeedbackTool'>;

/**
 *
 *
 *
 *
 *
 *
 *
 *
 *
 *
 *
 *
 *
 *
 *
 *
 *
 *
 *
 *
 *
 *
 *
 *
 *
 *
 */
export function mergeTracking(intents: IntentResult[]): ApiTrackingLabels {
    return intents.reduce<ApiTrackingLabels>((merged, intent) => {
        const tracking = intent.meta?.tracking;
        if (!tracking) return merged;

        for (const [key, values] of Object.entries(tracking)) {
            (merged[key] ??= []).push(...values);
        }

        return merged;
    }, {});
}

export function comparisonTrackingLabels(searchContext: SearchContext): ComparisonBaseTrackingLabels {
    const comparisonId = extractComparisonId(searchContext);

    if (comparisonId) {
        return { san_Comparison: true };
    }

    return {};
}

/**
 *
 *
 *
 *
 *
 *
 */
export function getHeaderTypeTracking(isMobile: boolean): HeaderTypeBaseTrackingLabels {
    return isMobile ? { san_HeaderType: 'mobile' } : {};
}

/**
 *
 *
 *
 *
 *
 *
 *
 */
export function getListFeedbackToolTracking(): ListFeedbackToolBaseTrackingLabels {
    return { 'wk.san_ListFeedbackTool': 'feedback_ltr' };
}

/**
 *
 *
 */
export function paginationTrackingLabels(
    sortOrder: string | undefined,
    currentPage: number,
    lastPage: number,
): PaginationBaseTrackingLabels {
    return {
        ...(sortOrder ? { 'wk.san_ListOrder': sortOrder } : {}),
        'wk.san_PageNumber': currentPage,
        'wk.san_CountPages': lastPage,
    };
}

/**
 *
 *
 */
export function additionalBaseTracking(isMobile: boolean): AdditionalBaseTrackingLabels {
    return {
        ...getHeaderTypeTracking(isMobile),
        ...getListFeedbackToolTracking(),
    };
}

/**
 *
 *
 */
export function topInfoTrackingLabels(searchContext: SearchContext): TopInfoBaseTrackingLabels {
    return { 'wk.san_TopInfoList': shouldShowTopInfos(searchContext) };
}

export function composeBaseTrackingLabels(input: {
    intents: IntentResult[];
    isMobile: boolean;
    searchContext: SearchContext;
    sortOrder: string | undefined;
    currentPage: number;
    lastPage: number;
    layout: GalleryLayout;
    onex?: OnEx;
    teaserVariationBoostShouldBeTracked: boolean;
}): TrackingLabels {
    const {
        intents,
        isMobile,
        searchContext,
        sortOrder,
        currentPage,
        lastPage,
        layout,
        onex,
        teaserVariationBoostShouldBeTracked,
    } = input;
    const experimentTracking = onex
        ? experimentSegmentTracking({ searchContext, layout, teaserVariationBoostShouldBeTracked }, onex)
        : {};

    return {
        ...experimentTracking,
        ...mergeTracking(intents),
        ...additionalBaseTracking(isMobile),
        ...comparisonTrackingLabels(searchContext),
        ...paginationTrackingLabels(sortOrder, currentPage, lastPage),
        ...topInfoTrackingLabels(searchContext),
    };
}
